import { AxiosResponse } from 'axios';
import axios from '|/helpers/axios';
import { PURCHASE_FUNDING_ENDPOINTS } from './endpoints';
import {
  ApprovedLenderOptionsResponse,
  CoApplicantToggleUpdateResponse,
  CoSignerEligibility,
  DownRecirculationResponse,
  DownRecirculationResult,
  IdentityPayload,
  IdentityResponse,
  Options,
  PaymentOptionsFinanceTerms,
  PaymentOptionsResponse,
  SafInstitutionResponse,
  SafLoanInformationInput,
  SelfArrangedFinanceLoanInput,
  Term,
  ThirdPartyLoanDetailsResponse
} from './types';

export const getThirdPartyLoanDetails = (abortSignal: AbortSignal) =>
  axios
    .get<ThirdPartyLoanDetailsResponse>(PURCHASE_FUNDING_ENDPOINTS.THIRD_PARTY_LOAN_DETAILS, {
      signal: abortSignal
    })
    .then(res => res.data);

export const saveThirdPartyLoanDetails = (
  payload: SelfArrangedFinanceLoanInput,
  abortSignal: AbortSignal
) =>
  axios
    .post<unknown>(
      PURCHASE_FUNDING_ENDPOINTS.THIRD_PARTY_LOAN_DETAILS,
      {
        ...payload
      },
      {
        signal: abortSignal
      }
    )
    .then(res => res.data);

export const saveSafLoanInformation = (
  payload: SafLoanInformationInput,
  abortSignal: AbortSignal
) =>
  axios
    .post<boolean>(PURCHASE_FUNDING_ENDPOINTS.SAVE_SAF_LOAN_INFORMATION, payload, {
      signal: abortSignal
    })
    .then(res => res.data);

export const getSafInstitutions = (abortSignal: AbortSignal) =>
  axios
    .get<SafInstitutionResponse[]>(PURCHASE_FUNDING_ENDPOINTS.GET_SAF_INSTITUTIONS, {
      signal: abortSignal
    })
    .then(res => res.data);

export const getApprovedLenderOptions = (abortSignal: AbortSignal) =>
  axios
    .get<ApprovedLenderOptionsResponse[]>(PURCHASE_FUNDING_ENDPOINTS.GET_APPROVED_LENDER_OPTIONS, {
      signal: abortSignal
    })
    .then(res => res.data);

// correlationId rides alongside the body, not inside it — it's a request header the backend reads
// into DownRecirculationEvent's Attribution.CorrelationId, not a field of the response.
// axios 1.7 — AxiosHeaders.get; the id is set bracket-style in helpers/axios
export const getDownRecirculation = (abortSignal: AbortSignal): Promise<DownRecirculationResult> =>
  axios
    .get<DownRecirculationResponse>(PURCHASE_FUNDING_ENDPOINTS.GET_DOWN_RECIRCULATION, {
      signal: abortSignal
    })
    .then(res => ({
      response: res.data,
      correlationId: res.config.headers.get('correlation-id')?.toString() ?? ''
    }));

export const getPaymentOptions = (abortSignal: AbortSignal) =>
  axios
    .get<PaymentOptionsResponse>(PURCHASE_FUNDING_ENDPOINTS.PAYMENT_OPTIONS, {
      signal: abortSignal
    })
    .then(res => res.data);

export const submitPaymentOption = (payload: Options, abortSignal: AbortSignal) =>
  axios.post(
    PURCHASE_FUNDING_ENDPOINTS.PAYMENT_OPTIONS,
    {
      type: payload.type,
      institutionName: payload.institutionName
    },
    {
      signal: abortSignal
    }
  );

export const getTerms = (abortSignal: AbortSignal) =>
  axios
    .get<PaymentOptionsFinanceTerms>(PURCHASE_FUNDING_ENDPOINTS.PAYMENT_TERMS, {
      signal: abortSignal
    })
    .then(res => res.data);

export const selectTerms = (payload: Term, abortSignal: AbortSignal) =>
  axios.post(PURCHASE_FUNDING_ENDPOINTS.PAYMENT_TERMS, payload, {
    signal: abortSignal
  });

export const getCoApplicantEligibility = (abortSignal: AbortSignal) =>
  axios
    .get<CoSignerEligibility>(PURCHASE_FUNDING_ENDPOINTS.COAPPLICANT_ELIGIBILITY, {
      signal: abortSignal
    })
    .then(res => res.data);

export const toggleCoApplicant = (abortSignal: AbortSignal) =>
  axios
    .post<never, AxiosResponse<CoApplicantToggleUpdateResponse>>(
      PURCHASE_FUNDING_ENDPOINTS.TOGGLE_COAPPLICANT,
      undefined,
      {
        signal: abortSignal
      }
    )
    .then(res => res.data);

export const getIdentity = (signal?: AbortSignal): Promise<IdentityResponse> =>
  axios
    .get<IdentityResponse>(PURCHASE_FUNDING_ENDPOINTS.IDENTITY, {
      signal
    })
    .then(res => res.data);

export const submitIdentityDetails = (
  payload: IdentityPayload,
  signal?: AbortSignal
): Promise<IdentityResponse> =>
  axios
    .post<IdentityResponse>(PURCHASE_FUNDING_ENDPOINTS.IDENTITY, payload, {
      headers: {
        'Content-Type': 'application/json'
      },
      signal
    })
    .then(res => res.data);
